import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { paymentSchema, type PriceFormValues } from '@eduzz-product-manager/validators';
import { zodResolver } from '@hookform/resolvers/zod';

import { FormBlock } from '@/components/FormBlock';
import { DEFAULT_MAXIMUM_LATE_PAYMENTS } from '@/env';
import { formatBrlMoney } from '@/lib/functions';
import useProductStore from '@/stores/productStore';

import { FreePayment } from './FreePayment';
import { OneTime } from './OneTime';
import { OpenPayment } from './OpenPayment';
import { Subscription } from './Subscription';
import ReviewByBillingModel from '../ReviewMode/ReviewByBillingModel';

export default function ProductPrice() {
  const [subtitle, setSubtitle] = useState('');
  const {
    price,
    paymentType,
    billingCycle,
    billingConfig,
    recurrenceCount,
    maximumLatePayments,
    activationFeeValue,
    trialDays
  } = useProductStore(p => ({
    price: p.payment.price,
    paymentType: p.payment.type,
    billingCycle: p.payment.billingCycle,
    recurrenceCount: p.payment.recurrenceCount,
    maximumLatePayments: p.payment.maximumLatePayments,
    activationFeeValue: p.payment.activationFeeValue,
    billingConfig: p.payment.billingConfig,
    trialDays: p.payment.trialDays
  }));

  const form = useForm<PriceFormValues>({
    resolver: zodResolver(paymentSchema) as any,
    mode: 'onChange',
    defaultValues: {
      type: paymentType ?? 'oneTime',
      price: price || 5,
      billingCycle: billingCycle ?? 'one_month',
      recurrenceCount: recurrenceCount ?? 0,
      maximumLatePayments: maximumLatePayments ?? DEFAULT_MAXIMUM_LATE_PAYMENTS,
      activationFeeValue: activationFeeValue ?? 5,
      trialDays: trialDays ?? 1,
      subscriptionBillingCycleQuantity: billingConfig?.value || 1,
      subscriptionBillingCycleType: billingConfig?.type || 'months',
      methods: [{ type: 'pix' }] // ignore this, its just to make the form happy
    }
  });

  useEffect(() => {
    form.setValue('paymentType', paymentType);

    if (price === undefined) {
      return;
    }

    if (paymentType === 'free') {
      setSubtitle('Grátis');
    }

    if (paymentType === 'open') {
      setSubtitle('Valor Aberto');
    }

    if (paymentType === 'oneTime') {
      setSubtitle(`Pagamento único de ${formatBrlMoney(price)}`);
    }

    const isSubscription = paymentType === 'subscription';

    if (isSubscription && billingCycle === 'one_month') {
      setSubtitle(`Assinatura mensal ${formatBrlMoney(price)}`);
    }

    if (isSubscription && billingCycle === 'three_months') {
      setSubtitle(`Assinatura trimestral de ${formatBrlMoney(price)}`);
    }

    if (isSubscription && billingCycle === 'six_months') {
      setSubtitle(`Assinatura semestral de ${formatBrlMoney(price)}`);
    }

    if (isSubscription && billingCycle === 'twelve_months') {
      setSubtitle(`Assinatura anual de ${formatBrlMoney(price)}`);
    }
  }, [paymentType, form, price, billingCycle]);

  return (
    <FormBlock
      name='product-price'
      subtitle={subtitle}
      form={form}
      title='Modelo de cobrança'
      preview={<ReviewByBillingModel />}
      allowEdit={paymentType !== 'free'}
    >
      {paymentType === 'open' && <OpenPayment />}
      {paymentType === 'oneTime' && <OneTime />}
      {paymentType === 'free' && <FreePayment />}
      {paymentType === 'subscription' && <Subscription />}
    </FormBlock>
  );
}
